import { ChangeDetectionStrategy, Component, computed, inject, model, OnInit, Signal } from '@angular/core';
import { DatePipe, NgClass } from '@angular/common';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { selectBulkValidationDetails, selectSelectedRowsIds } from '../../../state/location-list-selectors';
import { getElapsedTime } from '../restart-datasync/restart-datasync-utils';
import { BulkValidationPayload, BulkValidationStatus } from '../../../../../../models/bulk-validation-details';
import { Actions, ofType } from '@ngrx/effects';
import { toSignal } from '@angular/core/rxjs-interop';
import { BulkValidationActions } from '../../../state/location-list-actions';
import { CapitalizeFirstLetterPipe } from '../../../../../../pipes/capitalizeFirstLetter.pipe';
import { MatDivider } from '@angular/material/divider';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-bulk-validate',
  imports: [
    DatePipe,
    MatIcon,
    MatIconButton,
    MatMenu,
    MatProgressBar,
    MatProgressSpinner,
    MatTooltip,
    TranslateModule,
    MatMenuTrigger,
    NgClass,
    CapitalizeFirstLetterPipe,
    MatDivider,
    MatButtonToggleGroup,
    MatButtonToggle,
    FormsModule,
    MatButton,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './bulk-validate.component.html',
  styleUrls: ['./bulk-validate.component.scss',
    '../restart-datasync/restart-datasync.component.scss'],
})
export class BulkValidateComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly actions = inject(Actions);
  private readonly bulkValidateAction = toSignal(this.actions.pipe(ofType(
      BulkValidationActions.bulkValidate,
      BulkValidationActions.bulkValidateFail,
      BulkValidationActions.bulkValidateSuccess,
  )));
  private readonly bulkValidationDetailsAction = toSignal(this.actions.pipe(ofType(
      BulkValidationActions.getBulkValidationDetails,
      BulkValidationActions.getBulkValidationDetailsFail,
      BulkValidationActions.getBulkValidationDetailsSuccess,
  )));

  protected readonly bulkValidationDetails = this.store.selectSignal(selectBulkValidationDetails);
  protected readonly selectedLocationIds = this.store.selectSignal(selectSelectedRowsIds);

  protected readonly elapsedTime = computed(() => {
    const details = this.bulkValidationDetails();
    return getElapsedTime(details?.startTime, details?.lastUpdateTime);
  });

  // PROGRESS BAR & ICON style model
  protected readonly styleModel: Signal<{
    progressBarClass: string,
    iconClass: string,
    iconName: string
  }> = computed(() => {
    const details = this.bulkValidationDetails();

    switch (details?.status) {
      case BulkValidationStatus.IN_PROGRESS:
        return {
          progressBarClass: 'progressBarPending',
          iconClass: 'text-blue-50',
          iconName: 'bulk_validate_in_progress',
        };
      case BulkValidationStatus.COMPLETED:
        return {
          progressBarClass: 'progressBarFinished',
          iconClass: 'text-green-50',
          iconName: 'bulk_validate_completed',
        };
      default: // IDLE
        return {
          progressBarClass: 'progressBarNoSync',
          iconClass: 'text-neutral-40',
          iconName: 'bulk_validate_idle',
        };
    }
  });

  // REFRESH & VALIDATE BUTTON loading states
  protected readonly detailsLoading: Signal<boolean> = computed(() => {
    const action = this.bulkValidationDetailsAction();
    return action?.type === BulkValidationActions.getBulkValidationDetails.type;
  });

  protected readonly bulkValidateLoading: Signal<boolean> = computed(() => {
    const action = this.bulkValidateAction();
    return action?.type === BulkValidationActions.bulkValidate.type;
  });

  // VALIDATE BUTTON disabled state
  protected readonly validateButtonDisabled: Signal<boolean> = computed(() => {
    const detailsLoading = this.detailsLoading();
    const bulkValidateLoading = this.bulkValidateLoading();
    const details = this.bulkValidationDetails();
    const selectedLocationIds = this.selectedLocationIds();

    return detailsLoading ||
        bulkValidateLoading ||
        !selectedLocationIds?.length ||
        details?.status === BulkValidationStatus.IN_PROGRESS;
  });

  // BUTTON TOGGLE (action & coordinate src) models
  protected action = model<'VALIDATE' | 'INVALIDATE'>('VALIDATE');
  protected pinSource = model<'IMPORT' | 'GEOCODE'>('IMPORT');


  // ******************************************************************************************** //


  ngOnInit(): void {
    this.getDetails();
  }


  validate() {
    const payload: BulkValidationPayload = {
      locationIds: this.selectedLocationIds(),
      action: this.action(),
    };

    if (this.action() === 'VALIDATE') {
      payload.pinSource = this.pinSource();
    }

    this.store.dispatch(BulkValidationActions.bulkValidate({ payload }));
  }

  getDetails() {
    this.store.dispatch(BulkValidationActions.getBulkValidationDetails());
  }
}
